/**
 * Remembers which column was mapped to which field for files with the same headers
 * (shared by the lead import, spec-60 B2, and the CRM import, spec-63 E6). A convenience
 * in `localStorage`: it may be missing or throw, and then nothing remembers anything.
 */
export function mappingMemory<F extends string>(prefix: string, validFields: readonly F[]) {
  const storageKey = (headers: readonly string[]) =>
    prefix + headers.map((header) => header.trim().toLowerCase()).join('|')

  return {
    /** Remember the mapping for files with these headers (a convenience; may be lost). */
    remember(headers: readonly string[], mapping: readonly (F | null)[]): void {
      try {
        localStorage.setItem(storageKey(headers), JSON.stringify(mapping))
      } catch {
        // A private window or blocked storage: no memory, nothing breaks.
      }
    },
    /** The remembered mapping for exactly these headers, or null. */
    recall(headers: readonly string[]): (F | null)[] | null {
      try {
        const raw = localStorage.getItem(storageKey(headers))
        if (!raw) return null
        const value = JSON.parse(raw) as unknown
        const valid =
          Array.isArray(value) &&
          value.length === headers.length &&
          value.every((item) => item === null || validFields.includes(item as F))
        return valid ? (value as (F | null)[]) : null
      } catch {
        return null
      }
    },
  }
}
